import { useRef } from "react";
import { lovable } from "@/integrations/lovable";
import {
  isNativeGoogleAuthAvailable,
  signInWithNativeGoogle,
} from "@/lib/native-google-auth";
import { mapOAuthError } from "@/lib/oauth-signin";

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.3 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.3 2.8l5.7-5.7C33.6 6.5 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 13 24 13c2.8 0 5.4 1 7.3 2.8l5.7-5.7C33.6 6.5 29 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 43.5c5 0 9.5-1.9 13-5l-6-5c-1.9 1.4-4.4 2.3-7 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.1 16.2 43.5 24 43.5z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6 5C40.5 35.7 43.5 30.3 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

type Props = {
  busy: boolean;
  onStart: () => void;
  /** Called after the managed broker returns tokens in this same context. */
  onTokens: () => Promise<void> | void;
  onError: (message: string) => void;
};

/**
 * Managed Google sign-in. Web contexts use the managed broker directly; it
 * owns popup/redirect completion and writes the returned session. Inside the
 * native iOS app we use the SocialLogin plugin instead.
 */
export function GoogleSignInButton({ busy, onStart, onTokens, onError }: Props) {
  // Single-flight: a second broker call while one is open would consume the
  // same one-time authorization code twice and fail the exchange.
  const inFlight = useRef(false);

  const finish = () => {
    inFlight.current = false;
  };

  const click = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    onStart();
    try {
      // Inside the iOS app, use the native SocialLogin plugin (Google's sheet)
      // — the web OAuth redirect flow does not complete inside a WKWebView.
      if (isNativeGoogleAuthAvailable()) {
        const { error } = await signInWithNativeGoogle();
        finish();
        if (error) {
          onError(mapOAuthError(error, "Google"));
          return;
        }
        await onTokens();
        return;
      }
      // The managed helper supports both the Lovable preview and top-level
      // browsers. Call it directly from this user gesture so it can own the
      // popup, authorization-code exchange, and session handoff end to end.
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.redirected) return;
      if (result.error) {
        finish();
        onError(mapOAuthError(result.error, "Google"));
        return;
      }
      finish();
      await onTokens();
    } catch (e) {
      finish();
      onError(mapOAuthError(e, "Google"));
    }
  };

  return (
    <button
      type="button"
      onClick={() => void click()}
      disabled={busy}
      className="flex h-11 w-full items-center justify-center gap-3 rounded-xl glass-soft text-sm font-medium hover:bg-white/5 disabled:opacity-60"
    >
      <GoogleGlyph />
      {busy ? "Connecting…" : "Continue with Google"}
    </button>
  );
}
